import { ArrowLeft, ArrowRight, Aperture, Building2, Flame, AlertTriangle, Warehouse } from "lucide-react"
import { useWizard } from "./WizardContext"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const featureOptions = [
  { key: "hasSkylights" as const, label: "Skylights", desc: "Require new flashing", icon: Aperture, cost: "+$250/ea" },
  { key: "hasMultipleStories" as const, label: "2+ Stories", desc: "Additional labor for height access", icon: Building2, cost: "+$0.50/sqft" },
  { key: "hasChimney" as const, label: "Chimney", desc: "Flashing around chimney base", icon: Flame, cost: "+$450" },
  { key: "hasAsbestos" as const, label: "Asbestos", desc: "Suspected or known asbestos tiles", icon: AlertTriangle, cost: "+$2.50/sqft" },
]

const garageOptions = [
  { value: "none" as const, label: "No Garage", sqft: "" },
  { value: "small" as const, label: "Small", sqft: "~200 sqft" },
  { value: "medium" as const, label: "Medium", sqft: "~400 sqft" },
  { value: "large" as const, label: "Large", sqft: "~700 sqft" },
]

export default function FeaturesStep() {
  const { state, setFeatures, setGarage, nextStep, prevStep } = useWizard()

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          Any special features?
        </h2>
        <p className="text-muted-foreground mt-1">
          Select anything that applies. These affect the estimate.
        </p>
      </div>

      {/* Feature toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {featureOptions.map((feature) => {
          const isActive = state.features[feature.key]
          const Icon = feature.icon
          return (
            <button
              key={feature.key}
              onClick={() => setFeatures({ [feature.key]: !isActive })}
              className={cn(
                "flex items-start gap-3 p-4 border-2 rounded-xl text-left transition-all duration-200",
                isActive
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "border-muted hover:border-primary/30 hover:bg-primary/[0.02]",
              )}
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
                  isActive ? "bg-primary text-white" : "bg-muted text-muted-foreground",
                )}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground text-sm">{feature.label}</span>
                  <span className={cn("text-xs font-semibold", isActive ? "text-primary" : "text-muted-foreground")}>
                    {feature.cost}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{feature.desc}</p>
              </div>
            </button>
          )
        })}
      </div>

      {/* Detached garage */}
      <div>
        <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
          <Warehouse className="w-4 h-4 text-muted-foreground" />
          Detached garage?
        </h3>
        <div className="grid grid-cols-4 gap-2">
          {garageOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setGarage(opt.value)}
              className={cn(
                "py-3 px-2 border-2 rounded-xl text-center transition-all duration-200",
                state.detachedGarageSize === opt.value
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "border-muted hover:border-primary/30",
              )}
            >
              <div className="text-sm font-bold text-foreground">{opt.label}</div>
              {opt.sqft && <div className="text-[10px] text-muted-foreground mt-0.5">{opt.sqft}</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={prevStep} className="h-12 px-6 rounded-xl font-semibold">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={nextStep}
          className="flex-1 h-12 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold transition-all hover:shadow-lg hover:shadow-primary/25"
        >
          Continue
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  )
}
